import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/store/auth";
import { api } from "@/lib/api";
import { User, Package } from "lucide-react";

export default function Profile() {
  const { user, loading, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);

  useEffect(() => {
    if (!user) return;
    api.get("/me/orders", { withCredentials: true })
      .then((r) => setOrders(r.data.orders))
      .finally(() => setLoadingOrders(false));
  }, [user]);

  if (loading) return <div className="p-24 text-white/50">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="max-w-4xl mx-auto px-6 sm:px-10 py-12" data-testid="profile-page">
      <div className="flex items-center gap-6 mb-10">
        {user.picture ? (
          <img src={user.picture} alt="" className="w-20 h-20 rounded-full object-cover border border-[#FF3D8B]/40" />
        ) : (
          <div className="w-20 h-20 rounded-full bg-[#FF3D8B]/20 text-[#FF3D8B] flex items-center justify-center">
            <User size={28} />
          </div>
        )}
        <div className="flex-1">
          <h1 className="font-display text-4xl text-white leading-none" data-testid="profile-name">{user.name || "Welcome"}</h1>
          <div className="text-white/50 text-sm mt-1" data-testid="profile-email">{user.email}</div>
          {user.role === "owner" && (
            <span className="inline-block mt-2 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[#FF3D8B]/20 text-[#FF3D8B]">
              Owner
            </span>
          )}
        </div>
        <button
          onClick={logout}
          data-testid="profile-logout-btn"
          className="px-5 py-2 rounded-full border border-white/15 text-white/70 hover:border-[#FF3D8B] hover:text-[#FF3D8B] transition-colors text-sm"
        >
          Sign out
        </button>
      </div>

      <div className="flex items-center gap-2 mb-4 text-white/60 text-xs uppercase tracking-widest">
        <Package size={14} /> Purchase History
      </div>
      {loadingOrders ? (
        <div className="text-white/40 text-sm">Loading orders…</div>
      ) : orders.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-[#0f0f0f] p-8 text-center">
          <div className="text-white/60 text-sm mb-4">No orders yet.</div>
          <Link to="/shop" data-testid="profile-shop-cta"
            className="inline-block px-6 py-3 rounded-full bg-[#FF3D8B] text-black font-bold uppercase tracking-widest text-xs hover:bg-[#ff5ba0] transition-colors">
            Start Shopping
          </Link>
        </div>
      ) : (
        <ul className="space-y-3">
          {orders.map((o) => (
            <li key={o.session_id} className="rounded-xl border border-white/10 bg-[#0f0f0f] p-5" data-testid={`order-${o.session_id.slice(0,20)}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="text-white font-semibold">${o.amount?.toFixed(2)}</div>
                  <div className="text-white/40 text-xs">{(o.created_at || "").slice(0,16).replace("T", " ")}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${
                  o.payment_status === "paid" ? "bg-[#FF3D8B]/20 text-[#FF3D8B]" : "bg-white/5 text-white/50"
                }`}>{o.payment_status}</span>
              </div>
              <div className="text-white/60 text-sm">
                {(o.item_details || []).map((i, idx) =>
                  <span key={`${o.session_id}-${i.product_id || idx}`}>{i.name} × {i.quantity}{idx < o.item_details.length - 1 ? ", " : ""}</span>
                )}
              </div>
              {o.coupon_code && (
                <div className="text-[#FF3D8B] text-xs mt-1">Coupon: {o.coupon_code}</div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
